import { Badge, Group, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { currencyFormat } from "../../utils";

export interface FeeLine {
  collectedFeeId: string;
  orderId: string;
  orderNumber: string;
  baseAmount: string;
  appliedRate: string | null;
  finalFeeAmount: string;
  feeCurrency: string;
  invoiceRefCode?: string | null;
  orderCode?: string | null;
  paidDate?: string | null;
  counterpartyName?: string | null;
}

// Shared money/rate formatting — kept here so the modal total uses the same helpers.
export const money = (v: number | string, cur: string) =>
  `${currencyFormat(Number(v || 0)).replace(" ₮", "")} ${cur}`;
export const rate = (r: string | null | undefined) =>
  r ? `${(Number(r) * 100).toFixed(2)}%` : "";

// Readable identity for a fee line. Prefer the invoice ref + order code; never expose
// the raw UUID — fall back to a short masked tail when no human-readable code exists.
const maskedId = (id: string) => (id ? `#…${id.slice(-6)}` : "-");
const primaryLabel = (fee: FeeLine) => {
  const parts = [fee.invoiceRefCode, fee.orderCode].filter(Boolean);
  return parts.length ? parts.join(" · ") : maskedId(fee.orderId || fee.orderNumber);
};
const formatDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("mn-MN") : null;

interface Props {
  fee: FeeLine;
}

// Presentational fee line: identity (top-left) + dimmed date/base (bottom-left),
// fee amount + rate badge (right). Takes a single fee via props — no state.
export function FeePayLine({ fee }: Props) {
  const { t } = useTranslation("invoice");
  const date = formatDate(fee.paidDate);

  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Stack gap={2} miw={0}>
        <Text size="sm" fw={500} truncate>
          {primaryLabel(fee)}
        </Text>
        <Text size="xs" c="dimmed">
          {date ? `${date} · ` : ""}{t("feePay.baseAmount")}: {money(fee.baseAmount, fee.feeCurrency)}
        </Text>
      </Stack>

      <Group gap="xs" align="center" wrap="nowrap">
        {fee.appliedRate ? (
          <Badge variant="light" color="gray" radius="sm" size="sm">
            {rate(fee.appliedRate)}
          </Badge>
        ) : null}
        <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
          {money(fee.finalFeeAmount, fee.feeCurrency)}
        </Text>
      </Group>
    </Group>
  );
}
